<template>
  <div class="chat-header-container">
    <div
      :class="[
        'chat-header',
        {
          'chat-header-expanded': isXSmallScreenAndDown || participantsInputFocused,
        },
      ]"
    >
      <div class="chat-header-left" @click="participantsInputFocused = true">
        <FriedIconButton
          data-external="chat-header-back-button"
          class="hidden-xs-and-up"
          @click="$emit('show-conversation-list')"
        >
          <FriedIcon icon="chevron-left" />
        </FriedIconButton>
        <div v-if="creatingNewConversation" class="add-participants-container">
          <FriedParagraph class="description">{{ `${t('to')}:` }}</FriedParagraph>
          <AddParticipantsInput
            :participants="activeChatParticipants"
            :owner-company-name="ownerCompanyName"
            data-external="chat-header-add-participants-input"
            class="chat-header-title"
            :open-conversation-payload="openConversationPayload"
            @update-participants="$emit('update-participants', $event)"
            @focus="participantsInputFocused = true"
            @blur="participantsInputFocused = false"
          />
        </div>
        <div v-else class="chat-list-item">
          <div class="chat-list-item-information">
            <FriedH3>{{ title }} </FriedH3>
          </div>
        </div>
      </div>
      <FriedRoundedButton
        v-if="!creatingNewConversation"
        compact
        icon-align-right
        data-external="chat-header-show-participants-button"
        @click="$emit('show-conversation-participants-modal')"
      >
        <template #icon>
          <FriedIcon icon="users" />
        </template>
        {{ numberOfConversationParticipants }} {{ nrOfParticipantsString }}
      </FriedRoundedButton>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import type {
  ChatParticipant,
  ChatParticipantId,
  Conversation,
  OpenConversationPayload,
} from '../types';
import { ConversationType } from '../types';
import type { Participant } from '../../types';
import ChatHelper from '../helpers/chat.helper';
import AddParticipantsInput from './AddParticipantsInput.vue';

export default defineComponent({
  components: {
    AddParticipantsInput,
  },
  props: {
    conversation: {
      type: Object as PropType<Conversation>,
      default: undefined,
    },
    creatingNewConversation: {
      type: Boolean,
    },
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    activeChatParticipants: {
      type: Array as PropType<ChatParticipant[]>,
      default: () => [],
    },
    participantId: {
      type: String,
      default: '',
    },
    openConversationPayload: {
      type: Object as PropType<OpenConversationPayload>,
      default: null,
    },
    ownerCompanyName: {
      type: String,
      default: '',
    },
    currentUser: {
      type: Object as PropType<Participant>,
      default: () => ({}),
    },
    publishedView: {
      type: Boolean,
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: () =>
        ({
          participantId: '',
          userId: '',
        }) as ChatParticipantId,
    },
    numberOfConversationParticipants: {
      type: Number,
      default: 0,
    },
  },
  emits: ['show-conversation-list', 'update-participants', 'show-conversation-participants-modal'],
  setup(props) {
    const { t } = useI18n();

    const getParticipantNames = () =>
      ChatHelper.getParticipantNames({
        allParticipants: props.participants,
        conversationParticipantIds: props.conversation?.participantIds,
        currentParticipantId: props.currentChatUser?.participantId,
        currentUserId: props.currentChatUser?.userId,
      });

    const title = computed(() =>
      props.conversation.type === ConversationType.General
        ? t('dsr_chat_all_participants')
        : props.conversation.title || getParticipantNames()
    );

    const currentParticipant = computed(() => {
      if (props.participantId && props.publishedView) {
        return props.participants.find(r => r.id === props.participantId);
      }
      return null;
    });

    const currentUserId = computed(() =>
      props.publishedView ? props.participantId : props.currentChatUser.userId
    );

    const { isXSmallScreenAndDown } = useBreakpoints();

    const participantsInputFocused = ref(false);

    const nrOfParticipantsString = computed(() =>
      props.numberOfConversationParticipants === 1
        ? t('participant')
        : t('participants').toLocaleLowerCase()
    );

    return {
      getParticipantNames,
      title,
      ConversationType,
      t,
      participantsInputFocused,
      isXSmallScreenAndDown,
      currentParticipant,
      currentUserId,
      nrOfParticipantsString,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../scss/chat-list-item';

.chat-header-container {
  position: relative;

  > * {
    width: 100%;
  }

  .chat-header {
    display: flex;
    padding: var(--spacing-75) var(--spacing-150);
    align-items: center;
    height: 4rem;
    border-bottom: 1px solid var(--gray-92);

    @media screen and (max-width: $xs) {
      padding: var(--spacing-50) var(--spacing-100);

      &-expanded {
        height: 100%;
      }
    }

    .chat-list-item {
      height: auto;
      padding: 0;
    }

    .chat-header-left {
      display: flex;
      align-items: center;
      width: 100%;
      gap: var(--spacing-50);

      .add-participants-container {
        display: flex;
        align-items: center;
        overflow-x: scroll;
      }
    }
  }
}
</style>
